/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import Accordion, {AccordionProps} from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {MaterialExpandMoreIcon} from '@ion/dls/src/icons/MaterialExpandMoreIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';

// defines the prop definitions for the Accordion
export interface AccordionTypes extends Omit<AccordionProps, 'children'> {
  header: unknown;
  subHeader?: unknown;
  details: unknown;
  expanded?: boolean;
  borderBottom?: string;
  disableExpansion?: boolean;
  defaultExpanded?: boolean;
  padding?: string;
  marginBottom?: string
  onExpand?: (expanded: boolean) => void;
  isCondensedVariant?: boolean;
}

interface AccordionStyles {
  borderBottom?: string;
  padding?: string;
  marginBottom?: string;
  isCondensedVariant?: boolean;
}

// defines the styles used by the Accordion
const useStyles = makeStyles(theme => ({
  wrapperRoot : {
    width           : '100%',
    boxShadow       : '0px 0px !important',
    borderRadius    : '0px !important',
    backgroundColor : 'none !important',
    marginBottom    : ({marginBottom}: AccordionStyles) => marginBottom || '0'
  },
  summaryContent : {
    margin : '0 !important'
  },
  condensedSummaryContent : {
    margin    : '0 !important',
    maxHeight : 48
  },
  detailsRoot : {
    display   : 'block',
    borderTop : `solid 1px ${dlsColor('grey5')}`,
    padding   : ({padding}: AccordionStyles) => padding || '10px 0px 25px'
  },
  expanded : {
    borderBottom : ({borderBottom}: AccordionStyles) => borderBottom || `solid 1px ${dlsColor('grey6')}`,
    '&$expanded' : {
      minHeight : ({isCondensedVariant}: AccordionStyles) => isCondensedVariant ? '48px' : '64px'
    }
  },
  disabledState : {
    '& .MuiAccordionSummary-expandIcon' : {
      display : 'none'
    },
    pointerEvents : 'none'
  }
}));

export const getAccordionWrapperStyles = (
  borderStyle: string = `1px solid ${dlsColor('grey5')}`
) => ({
  accordionWrapper : {
    borderBottom : borderStyle
  }
});

/**
 * @Accordion
 *
 * Defines the Accordion component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 *   import Accordion from '@ion/dls/src/components/accordion/Accordion';
 *   <Accordion children=''
 *     header={}
 *     details={} />
 * ```
 */
const AccordionWrapper = ({
  header,
  subHeader,
  details,
  expanded,
  borderBottom,
  defaultExpanded,
  padding,
  marginBottom,
  disableExpansion,
  onExpand,
  isCondensedVariant,
  className
}: AccordionTypes) => {
  const classes = useStyles({borderBottom, padding, marginBottom, isCondensedVariant});
  const [isExpanded, setIsExpanded] = useState(false);

  const onHandleChange = useCallback(function(event, expanded) {
    onExpand?.(expanded);
    setIsExpanded(expanded);
  }, []);

  return (
    <Accordion classes={{root: classes.wrapperRoot}}
      defaultExpanded={defaultExpanded || expanded || isExpanded}
      expanded={expanded}
      onChange={onHandleChange}>
      <AccordionSummary expandIcon={<MaterialExpandMoreIcon />}
        classes={{
          content : isCondensedVariant ? classes.condensedSummaryContent : classes.summaryContent,
          root    : classnames(className, disableExpansion ? classes.disabledState : classes.expanded)
        }}>
        <>
          {header}
          {subHeader}
        </>
      </AccordionSummary>
      <AccordionDetails classes={{root: classes.detailsRoot}}>
        {details}
      </AccordionDetails>
    </Accordion>
  );
};

export default AccordionWrapper;
